import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const faqData = [
  {
    category: '碳排放申报',
    questions: [
      {
        q: '工信部碳排放申报需要哪些基础数据？',
        a: '主要需要能源消费数据（电力、天然气、煤炭等各类能源的消费量）、生产数据（产量、产值）、设备运行数据（运行时长、功率）等。Firmai系统可通过IoT传感器自动采集绝大部分数据，减少人工录入工作量。',
      },
      {
        q: '没有IoT设备的工厂如何接入Firmai系统？',
        a: '我们提供多种数据接入方式：①IoT传感器安装（我们提供硬件支持）②通过现有能源管理系统API对接③Excel/CSV手动导入④与公用事业账单对接。可以根据工厂实际情况灵活选择。',
      },
      {
        q: '12项功能中，哪些功能是工信部评估必须具备的？',
        a: '工信部建设指南要求12项功能均需具备，其中能耗查询、能源消费量计算、碳排放核算是核心基础功能，其余功能需根据企业规模和行业类型选择性部署。Firmai提供12项功能全覆盖，确保100%通过评估。',
      },
    ],
  },
  {
    category: 'CCER/CBAM',
    questions: [
      {
        q: '我的工厂是否适合开发CCER项目？',
        a: 'CCER适合具备可量化减排量的项目，如光伏发电、风电、工业节能改造、碳捕集等。建议先联系我们进行免费适用性评估，专家团队会根据您的生产情况判断是否具备CCER开发条件及预估可开发量。',
      },
      {
        q: 'CBAM影响哪些行业和产品？',
        a: '目前CBAM涵盖钢铁（含钢铁制品）、铝及铝制品、水泥、化肥、电力、氢等6大类产品。2026年起进入付费阶段，出口欧盟企业需申报产品内嵌碳并购买CBAM证书。',
      },
      {
        q: 'CCER数据上报到全国注册登记系统的具体流程是什么？',
        a: '流程包括：项目设计→业主申请备案→第三方核查机构核查→生态环境部审核→项目注册→监测计划实施→减排量核证→签发→转让/注销。Firmai支持全流程数据准备与系统直连提交。',
      },
    ],
  },
  {
    category: '工业AI',
    questions: [
      {
        q: 'AI预测性运维的预测准确率能达到多少？',
        a: '在充分训练数据的情况下，Firmai AI预测性运维模型对常见设备故障的预测准确率可达85-95%，通常可提前7-30天发现潜在故障，帮助企业将非计划停机次数降低60%以上。',
      },
      {
        q: '工业AI系统需要多长时间部署？',
        a: '轻量化部署方案通常2-4周即可完成初步上线，完整的定制化开发项目根据场景复杂度一般需要2-6个月。我们提供分阶段交付，确保核心功能优先上线，快速产生价值。',
      },
    ],
  },
];

const downloads = [
  { title: '工信部碳排放申报数据填报模板（2025版）', type: 'Excel', size: '245 KB', icon: 'ri-file-excel-2-line', color: 'text-green-400' },
  { title: 'CCER项目申报资料清单及模板包', type: 'ZIP', size: '3.2 MB', icon: 'ri-folder-zip-line', color: 'text-amber-400' },
  { title: 'CBAM合规检查自查表（中英双语）', type: 'PDF', size: '890 KB', icon: 'ri-file-pdf-line', color: 'text-red-400' },
  { title: 'Firmai碳排放申报管理系统操作手册', type: 'PDF', size: '12.3 MB', icon: 'ri-file-pdf-line', color: 'text-red-400' },
  { title: '工业AI平台部署配置指南', type: 'PDF', size: '5.6 MB', icon: 'ri-file-pdf-line', color: 'text-red-400' },
  { title: '企业碳盘查数据采集台账模板', type: 'Excel', size: '178 KB', icon: 'ri-file-excel-2-line', color: 'text-green-400' },
  { title: '数字化能碳管理中心建设指南（工信部原文）', type: 'PDF', size: '2.1 MB', icon: 'ri-file-pdf-line', color: 'text-red-400' },
  { title: 'Firmai API接口开发文档v2.0', type: 'PDF', size: '8.4 MB', icon: 'ri-file-pdf-line', color: 'text-red-400' },
];

const serviceProcess = [
  { step: '01', icon: 'ri-customer-service-2-line', title: '售前咨询', desc: '专属顾问免费评估需求，30分钟出具初步方案' },
  { step: '02', icon: 'ri-draft-line', title: '方案定制', desc: '技术+商务团队联合制定详细实施方案与报价' },
  { step: '03', icon: 'ri-rocket-line', title: '项目落地', desc: '专业实施团队现场或远程交付，确保按时上线' },
  { step: '04', icon: 'ri-tools-line', title: '售后运维', desc: '7×24小时技术支持，定期系统优化与功能迭代' },
];

export default function SupportPage() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState<string | null>(null);

  const toggleFaq = (key: string) => setOpenFaq(openFaq === key ? null : key);

  return (
    <div className="min-h-screen bg-firmai-dark">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-28 pb-14 overflow-hidden">
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(0,217,163,0.15) 1px, transparent 0)', backgroundSize: '40px 40px' }} />
          </div>
          <div className="w-full px-4 md:px-8 lg:px-12 relative z-10 text-center">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-firmai-green/10 border border-firmai-green/25 text-firmai-green text-xs font-semibold mb-5">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-customer-service-2-line" /></span>
              全周期服务支持
            </span>
            <h1 className="font-display font-bold text-3xl md:text-5xl text-white mb-4">服务支持</h1>
            <p className="text-firmai-text text-base md:text-lg max-w-2xl mx-auto">
              提供在线客服、资料下载、常见问题解答、培训服务等全方位支持，确保您的业务顺利运营
            </p>
          </div>
        </section>

        {/* Quick Actions */}
        <section className="py-10 bg-firmai-darker border-b border-firmai-border">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { icon: 'ri-wechat-line', title: '在线客服咨询', desc: '实时在线，快速响应', action: () => navigate('/contact'), actionLabel: '立即咨询' },
                { icon: 'ri-phone-line', title: '服务热线', desc: '18916035855', action: () => {}, actionLabel: '拨打电话' },
                { icon: 'ri-download-cloud-line', title: '资料下载', desc: '模板、手册、规范', action: () => {}, actionLabel: '前往下载' },
                { icon: 'ri-graduation-cap-line', title: '培训服务', desc: '线上/线下培训', action: () => navigate('/contact'), actionLabel: '预约培训' },
              ].map((item) => (
                <button
                  key={item.title}
                  onClick={item.action}
                  className="bg-firmai-card border border-firmai-border rounded-xl p-5 text-left hover:border-firmai-green/40 transition-all duration-200 cursor-pointer group"
                >
                  <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-firmai-green/10 text-firmai-green mb-3 group-hover:bg-firmai-green/20 transition-colors">
                    <i className={`${item.icon} text-xl`} />
                  </span>
                  <div className="text-white text-sm font-semibold mb-1">{item.title}</div>
                  <div className="text-firmai-muted text-xs">{item.desc}</div>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Service Process */}
        <section className="py-14 bg-firmai-dark">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <h2 className="text-white font-bold text-xl md:text-2xl mb-8 text-center">服务流程</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {serviceProcess.map((s, idx) => (
                <div key={s.step} className="relative bg-firmai-card border border-firmai-border rounded-xl p-5 text-center">
                  {idx < serviceProcess.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-2 w-4 h-0.5 bg-firmai-green/40 z-10" />
                  )}
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-firmai-green/10 border border-firmai-green/30 text-firmai-green mx-auto mb-3">
                    <i className={`${s.icon} text-xl`} />
                  </div>
                  <div className="text-firmai-muted text-xs mb-1">STEP {s.step}</div>
                  <div className="text-white font-semibold text-sm mb-2">{s.title}</div>
                  <div className="text-firmai-muted text-xs leading-relaxed">{s.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Downloads */}
        <section className="py-14 bg-firmai-darker">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <h2 className="text-white font-bold text-xl md:text-2xl mb-2">资料下载</h2>
            <p className="text-firmai-muted text-sm mb-6">申报模板、台账表格、操作手册等实用资料免费下载</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {downloads.map((d) => (
                <div key={d.title} className="flex items-center gap-4 bg-firmai-card border border-firmai-border rounded-xl p-4 hover:border-firmai-green/30 transition-all duration-200 cursor-pointer group">
                  <span className={`w-10 h-10 flex items-center justify-center shrink-0 ${d.color}`}>
                    <i className={`${d.icon} text-2xl`} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-sm font-medium leading-snug truncate">{d.title}</div>
                    <div className="text-firmai-muted text-xs mt-0.5">{d.type} · {d.size}</div>
                  </div>
                  <span className="w-8 h-8 flex items-center justify-center text-firmai-muted group-hover:text-firmai-green transition-colors shrink-0">
                    <i className="ri-download-2-line" />
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-14 bg-firmai-dark">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <h2 className="text-white font-bold text-xl md:text-2xl mb-2">常见问题</h2>
            <p className="text-firmai-muted text-sm mb-8">碳申报、工业AI、CCER/CBAM相关问题解答</p>
            <div className="max-w-4xl space-y-4">
              {faqData.map((cat) => (
                <div key={cat.category}>
                  <h3 className="text-firmai-green text-sm font-semibold mb-3 flex items-center gap-2">
                    <span className="w-1 h-4 bg-firmai-green rounded-full block" />
                    {cat.category}
                  </h3>
                  <div className="space-y-2">
                    {cat.questions.map((faq, idx) => {
                      const key = `${cat.category}-${idx}`;
                      const isOpen = openFaq === key;
                      return (
                        <div key={key} className="bg-firmai-card border border-firmai-border rounded-xl overflow-hidden">
                          <button
                            onClick={() => toggleFaq(key)}
                            className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer hover:bg-white/3 transition-colors"
                          >
                            <span className="text-white text-sm font-medium leading-snug">{faq.q}</span>
                            <span className={`w-5 h-5 flex items-center justify-center text-firmai-muted shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
                              <i className="ri-arrow-down-s-line" />
                            </span>
                          </button>
                          {isOpen && (
                            <div className="px-5 pb-4 text-firmai-text text-sm leading-relaxed border-t border-firmai-border pt-3">
                              {faq.a}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-14 bg-firmai-darker">
          <div className="w-full px-4 md:px-8 lg:px-12 text-center">
            <h2 className="text-white font-bold text-xl md:text-2xl mb-4">没有找到答案？联系我们的专家团队</h2>
            <p className="text-firmai-muted text-sm mb-6">7×24小时技术支持，专业顾问实时解答您的问题</p>
            <a
              href="/contact"
              onClick={(e) => { e.preventDefault(); navigate('/contact'); }}
              className="inline-flex items-center gap-2 px-7 py-3 bg-firmai-green text-firmai-dark font-semibold rounded-md hover:bg-firmai-green/90 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              联系专家
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line" /></span>
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}